import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  deleteLeaderboardPrize,
  listAdminArticles,
  listLeaderboardPrizes,
  upsertLeaderboardPrize,
  type Article,
  type LeaderboardPrize,
} from '../lib/admin';

const CANTONS: Array<{ code: string; name: string }> = [
  { code: 'ZH', name: 'Zürich' },        { code: 'BE', name: 'Bern' },
  { code: 'LU', name: 'Lucerne' },       { code: 'UR', name: 'Uri' },
  { code: 'SZ', name: 'Schwyz' },        { code: 'OW', name: 'Obwalden' },
  { code: 'NW', name: 'Nidwalden' },     { code: 'GL', name: 'Glarus' },
  { code: 'ZG', name: 'Zug' },           { code: 'FR', name: 'Fribourg' },
  { code: 'SO', name: 'Solothurn' },     { code: 'BS', name: 'Basel-Stadt' },
  { code: 'BL', name: 'Basel-Landschaft' },{ code: 'SH', name: 'Schaffhausen' },
  { code: 'AR', name: 'Appenzell AR' },  { code: 'AI', name: 'Appenzell IR' },
  { code: 'SG', name: 'St. Gallen' },    { code: 'GR', name: 'Graubünden' },
  { code: 'AG', name: 'Aargau' },        { code: 'TG', name: 'Thurgau' },
  { code: 'TI', name: 'Ticino' },        { code: 'VD', name: 'Vaud' },
  { code: 'VS', name: 'Valais' },        { code: 'NE', name: 'Neuchâtel' },
  { code: 'GE', name: 'Geneva' },        { code: 'JU', name: 'Jura' },
];

type BucketKey = 'global' | string; // scope value

export function AdminPrizes() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [prizes, setPrizes] = useState<LeaderboardPrize[] | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ scope: BucketKey; prize: LeaderboardPrize | null; position?: number } | null>(null);
  const [addCanton, setAddCanton] = useState<string>('');

  async function load() {
    if (!accessToken) return;
    setErr(null);
    try {
      const [pr, ar] = await Promise.all([
        listLeaderboardPrizes(accessToken),
        listAdminArticles(accessToken, { activeOnly: true }),
      ]);
      setPrizes(pr);
      setArticles(ar);
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => { load(); }, [accessToken]);

  // Group prizes by scope and sort each bucket by position. We render
  // every canton that has at least one prize plus an "Add canton" row at
  // the bottom so admins can spin up new buckets on demand.
  const buckets = useMemo(() => {
    const m = new Map<BucketKey, LeaderboardPrize[]>();
    m.set('global', []);
    for (const p of prizes ?? []) {
      const arr = m.get(p.scope) ?? [];
      arr.push(p);
      m.set(p.scope, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.position - b.position);
    return m;
  }, [prizes]);

  const cantonsInUse = useMemo(() => {
    const set = new Set<string>();
    for (const k of buckets.keys()) if (k !== 'global') set.add(k);
    return set;
  }, [buckets]);

  const availableCantons = useMemo(
    () => CANTONS.filter((c) => !cantonsInUse.has(c.code)),
    [cantonsInUse],
  );

  async function onDelete(id: number) {
    if (!accessToken) return;
    if (!confirm('Remove this prize assignment? The article itself isn\'t deleted.')) return;
    try {
      await deleteLeaderboardPrize(accessToken, id);
      await load();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    }
  }

  function nextPositionFor(scope: BucketKey): number {
    const arr = buckets.get(scope) ?? [];
    return arr.length === 0 ? 1 : Math.max(...arr.map((p) => p.position)) + 1;
  }

  function bucketLabel(scope: BucketKey): string {
    if (scope === 'global') return 'Switzerland · global leaderboard';
    const m = CANTONS.find((c) => c.code === scope);
    return m ? `${scope} · ${m.name}` : scope;
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Leaderboard prizes</h1>
          </header>

          <p class="admin-lede">
            Pick which <a class="admin-link" href={localePath('/admin/articles')}>articles</a> get awarded to which positions on the
            global leaderboard and per-canton leaderboards. Awarding is manual
            — you'll contact winners offline once the tournament closes.
          </p>

          {err && <p class="admin-error">{err}</p>}
          {!prizes && !err && <p class="admin-lede">Loading…</p>}

          {prizes && (
            <>
              {[...buckets.entries()].map(([scope, arr]) => (
                <PrizeBucket
                  key={scope}
                  scope={scope}
                  label={bucketLabel(scope)}
                  prizes={arr}
                  onAdd={() => setEditing({ scope, prize: null, position: nextPositionFor(scope) })}
                  onEdit={(p) => setEditing({ scope, prize: p })}
                  onDelete={onDelete}
                />
              ))}

              {availableCantons.length > 0 && (
                <div style={{ marginTop: 24, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span class="admin-mono" style={{ fontSize: 11, color: 'rgba(245,237,224,0.6)' }}>
                    Add a canton bucket:
                  </span>
                  <select
                    class="admin-filter-select"
                    value={addCanton}
                    onChange={(e) => setAddCanton((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">— pick canton —</option>
                    {availableCantons.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                  </select>
                  <button
                    class="admin-primary"
                    disabled={!addCanton}
                    onClick={() => {
                      setEditing({ scope: addCanton, prize: null, position: 1 });
                      setAddCanton('');
                    }}
                  >
                    + First prize
                  </button>
                </div>
              )}
            </>
          )}

          {editing && accessToken && (
            <PrizeModal
              scope={editing.scope}
              scopeLabel={bucketLabel(editing.scope)}
              prize={editing.prize}
              defaultPosition={editing.position}
              articles={articles}
              accessToken={accessToken}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function PrizeBucket({
  scope,
  label,
  prizes,
  onAdd,
  onEdit,
  onDelete,
}: {
  scope: BucketKey;
  label: string;
  prizes: LeaderboardPrize[];
  onAdd: () => void;
  onEdit: (p: LeaderboardPrize) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <section style={{ marginTop: 28 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: scope === 'global' ? '#E87B1E' : '#f5ede0' }}>
          {label}
        </h2>
        <button class="admin-link" onClick={onAdd}>+ Add position</button>
      </header>
      {prizes.length === 0 ? (
        <p class="admin-lede" style={{ marginTop: 0 }}>No prizes set on this leaderboard yet.</p>
      ) : (
        <table class="admin-table">
          <thead>
            <tr>
              <th style={{ width: 64 }}>#</th>
              <th />
              <th>Article</th>
              <th>Partner</th>
              <th>Kind</th>
              <th>Value</th>
              <th>Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {prizes.map((p) => (
              <tr key={p.id}>
                <td><strong style={{ fontSize: 18 }}>{p.position}</strong></td>
                <td>
                  {p.articleImage ? (
                    <img
                      src={p.articleImage}
                      alt=""
                      style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : (
                    <span style={{ opacity: 0.3 }}>—</span>
                  )}
                </td>
                <td>{p.articleTitle}</td>
                <td>{p.partnerName ?? <span style={{ opacity: 0.6 }}>BeerCup</span>}</td>
                <td>{p.articleKind}</td>
                <td class="admin-mono">{p.valueChf != null ? `CHF ${p.valueChf.toFixed(2)}` : '—'}</td>
                <td class="admin-mono" style={{ fontSize: 11, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.notes || '—'}
                </td>
                <td>
                  <button class="admin-link" onClick={() => onEdit(p)} style={{ marginRight: 10 }}>Edit</button>
                  <button class="admin-link" onClick={() => onDelete(p.id)} style={{ color: '#ff8b6b' }}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PrizeModal({
  scope,
  scopeLabel,
  prize,
  defaultPosition,
  articles,
  accessToken,
  onClose,
  onSaved,
}: {
  scope: BucketKey;
  scopeLabel: string;
  prize: LeaderboardPrize | null;
  defaultPosition?: number;
  articles: Article[];
  accessToken: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const isNew = prize === null;
  const [position, setPosition] = useState(prize?.position ?? defaultPosition ?? 1);
  const [articleId, setArticleId] = useState<number | ''>(prize?.articleId ?? '');
  const [notes, setNotes] = useState(prize?.notes ?? '');
  const [partnerFilter, setPartnerFilter] = useState<string>(''); // by partner name fuzzy filter
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const filteredArticles = useMemo(() => {
    const needle = partnerFilter.trim().toLowerCase();
    if (!needle) return articles;
    return articles.filter((a) => {
      const hay = (a.partnerName ?? 'beercup').toLowerCase();
      return hay.includes(needle);
    });
  }, [articles, partnerFilter]);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (position <= 0) throw new Error('Position must be ≥ 1');
      if (!articleId) throw new Error('Pick an article');
      await upsertLeaderboardPrize(accessToken, {
        scope,
        position,
        articleId: Number(articleId),
        notes: notes.trim() || undefined,
      });
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>{isNew ? 'Add prize position' : 'Edit prize'}</h2>
        <dl class="admin-meta">
          <div>
            <dt>Leaderboard</dt>
            <dd>{scopeLabel}</dd>
          </div>
        </dl>

        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
          <label class="admin-field">
            <span>Position</span>
            <input
              type="number"
              min={1}
              value={position}
              onInput={(e) => setPosition(parseInt((e.target as HTMLInputElement).value, 10) || 1)}
              disabled={!isNew}
            />
          </label>
          <label class="admin-field">
            <span>Filter articles by partner</span>
            <input
              value={partnerFilter}
              onInput={(e) => setPartnerFilter((e.target as HTMLInputElement).value)}
              placeholder="type a partner name…"
            />
          </label>
        </div>

        <label class="admin-field">
          <span>Article</span>
          <select
            value={articleId === '' ? '' : String(articleId)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setArticleId(v === '' ? '' : Number(v));
            }}
          >
            <option value="">— pick an article —</option>
            {filteredArticles.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title} · {a.kind} · {a.partnerName ?? 'BeerCup'}{a.valueChf != null ? ` · CHF ${a.valueChf.toFixed(2)}` : ''}
              </option>
            ))}
          </select>
        </label>

        <label class="admin-field">
          <span>Notes (admin only)</span>
          <textarea rows={2} value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </label>

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button type="submit" class="admin-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Add prize' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
